import { CLINIC } from "@/data/clinic";
import AppointmentForm from "@/components/AppointmentForm";
import { MapPin, Phone, Mail, Clock, MessageCircle, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import SEOHead, { physicianSchema, localBusinessSchema } from "@/components/SEOHead";

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <SEOHead
        title="Contact Us | Dr. Ashutosh Mishra — Diabetes & Endocrinology Clinic, Varanasi"
        description="Book an appointment with Dr. Ashutosh Mishra. Clinic in Kabir Nagar, Durgakund Road, Varanasi 221005. Call +91 96218 50425 or message on WhatsApp."
        path="/contact"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Contact", url: "/contact" },
        ]}
        schemas={[
          { id: "ld-physician", data: physicianSchema() },
          { id: "ld-localbiz", data: localBusinessSchema() },
        ]}
      />
      <section className="bg-gradient-to-br from-green-50/60 via-white to-green-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
          <nav className="text-xs text-slate-500 mb-3 text-left">
            <Link to="/" className="hover:text-green-700">Home</Link> <span className="mx-1.5">/</span> <span className="text-slate-700">Contact</span>
          </nav>
          <div className="text-xs uppercase tracking-[0.22em] text-green-700 font-medium">Get in touch</div>
          <h1 className="font-poppins font-semibold text-4xl sm:text-5xl lg:text-6xl text-slate-900 mt-3">Book an Appointment</h1>
          <p className="text-slate-600 text-lg mt-4 max-w-2xl mx-auto">
            Submit the form below, call us, or send a WhatsApp message — we will confirm your slot the same day.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3">
          <AppointmentForm />
        </div>
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white border border-slate-100 rounded-3xl p-6">
            <div className="flex gap-4">
              <div className="w-11 h-11 rounded-2xl bg-green-50 text-green-700 grid place-items-center"><MapPin className="w-5 h-5" /></div>
              <div>
                <h2 className="font-medium text-slate-900 font-poppins">Clinic Address</h2>
                <div className="text-sm text-slate-600 mt-1 leading-relaxed">{CLINIC.address}</div>
                <a href={CLINIC.mapLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-green-700 mt-2">Open in Google Maps <ExternalLink className="w-3 h-3" /></a>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-100 rounded-3xl p-6">
            <div className="flex gap-4">
              <div className="w-11 h-11 rounded-2xl bg-green-50 text-green-700 grid place-items-center"><Phone className="w-5 h-5" /></div>
              <div>
                <h2 className="font-medium text-slate-900 font-poppins">Phone</h2>
                <a href={`tel:+91${CLINIC.phone}`} className="text-sm text-green-700 mt-1 block">{CLINIC.phoneDisplay}</a>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-100 rounded-3xl p-6">
            <div className="flex gap-4">
              <div className="w-11 h-11 rounded-2xl bg-emerald-50 text-emerald-700 grid place-items-center"><MessageCircle className="w-5 h-5" /></div>
              <div>
                <h2 className="font-medium text-slate-900 font-poppins">WhatsApp</h2>
                <a href={`https://wa.me/${CLINIC.whatsapp}`} target="_blank" rel="noreferrer" className="text-sm text-emerald-700 mt-1 block">Chat with us instantly</a>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-100 rounded-3xl p-6">
            <div className="flex gap-4">
              <div className="w-11 h-11 rounded-2xl bg-green-50 text-green-700 grid place-items-center"><Mail className="w-5 h-5" /></div>
              <div>
                <h2 className="font-medium text-slate-900 font-poppins">Email</h2>
                <a href={`mailto:${CLINIC.email}`} className="text-sm text-green-700 mt-1 block">{CLINIC.email}</a>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-100 rounded-3xl p-6">
            <div className="flex gap-4">
              <div className="w-11 h-11 rounded-2xl bg-green-50 text-green-700 grid place-items-center"><Clock className="w-5 h-5" /></div>
              <div>
                <h2 className="font-medium text-slate-900 font-poppins">Clinic Hours</h2>
                <div className="text-sm text-slate-600 mt-1 space-y-1">
                  {CLINIC.hours.map((h) => <div key={h.day}><span className="text-slate-900 font-medium">{h.day}:</span> {h.time}</div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-poppins font-semibold text-3xl text-slate-900 mb-6">Find Us on the Map</h2>
          <div className="rounded-3xl overflow-hidden border border-slate-200">
            <iframe
              title="contact-map"
              src={CLINIC.mapEmbed}
              width="100%"
              height="420"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <div className="mt-4 text-sm">
            <a href={CLINIC.mapLink} target="_blank" rel="noreferrer" className="text-green-700 inline-flex items-center gap-1">
              Open in Google Maps <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
